import React, { useState, ChangeEvent } from 'react';
import { useDispatch } from 'react-redux';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { useChatMutation } from '../../store/services/agentAssistantService';
import { setAgentAssistant } from '../../store/features/agentAssistantSlice';
import SpeechButton from '../common/SpeechButton';

interface Message {
  content: string;
  agent: string;
}

interface AgentAssistantResponse {
  conversation_id: string;
  current_agent: string;
  messages: Message[];
  events: any[];
  context: any[];
  guardrails: any[];
}

const AgentAssistantConversation = () => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [clearTranscriptFn, setClearTranscriptFn] = useState<(() => void) | null>(null);
  const [chat, { isLoading }] = useChatMutation();

  const handleToggle = () => {
    if (!isOpen) {
      // Reset conversation when opening
      setConversationId(null);
      setMessages([]);
      setInputValue('');
    }
    setIsOpen(!isOpen);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    
    // Clear transcript when user manually clears the input
    if (!newValue && clearTranscriptFn) {
      clearTranscriptFn();
    }
  };

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!inputValue.trim()) return;

      const userMessage: Message = {
        content: inputValue,
        agent: 'User'
      };

      // Add user message to the conversation
      setMessages(prev => [...prev, userMessage]);

      try {
        const response = await chat({
          conversation_id: conversationId,
          message: inputValue
        }).unwrap() as AgentAssistantResponse;

        // Update conversation ID if this is the first message
        if (!conversationId && response.conversation_id) {
          setConversationId(response.conversation_id);
        }

        // Add agent messages to the conversation
        if (response.messages && response.messages.length > 0) {
          setMessages(prev => [...prev, ...response.messages]);
        }

        // Update the store
        dispatch(setAgentAssistant(response));

        // Clear input and transcript
        setInputValue('');
        if (clearTranscriptFn) {
          clearTranscriptFn();
        }
      } catch (error) {
        console.error('Error sending message:', error);
        // Add error message to conversation
        setMessages(prev => [...prev, {
          content: 'Sorry, there was an error processing your request.',
          agent: 'System'
        }]);
      }
    }
  };

  const handleSpeechResult = (text: string) => {
    setInputValue(text);
  };

  const handleSpeechPartialResult = (text: string) => {
    setInputValue(text);
  };

  // Add a function to clear the input when starting speech
  const handleStartListening = () => {
    setInputValue(''); // Clear the input field
  };

  // Handle setting the clearTranscript function from SpeechButton
  const handleClearTranscript = (clearTranscript: () => void) => {
    setClearTranscriptFn(() => clearTranscript);
  };

  return (
    <React.Fragment>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Button variant="outlined" onClick={handleToggle}>
          New Agent Conversation
        </Button>
      </div>
      <Dialog open={isOpen} onClose={handleToggle} fullWidth maxWidth="md">
        <DialogTitle>Agent Assistant Conversation</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <div style={{ 
                height: '400px', 
                overflowY: 'auto', 
                border: '1px solid #ccc', 
                padding: '10px',
                marginBottom: '10px',
                backgroundColor: '#f9f9f9'
              }}>
                {messages.length === 0 && (
                  <div style={{ textAlign: 'center', color: '#666', marginTop: '20px' }}>
                    Start a conversation with the Agent Assistant...
                  </div>
                )}
                {messages.map((message, index) => (
                  <div key={index} style={{ 
                    marginBottom: '10px',
                    padding: '8px',
                    backgroundColor: message.agent === 'User' ? '#e3f2fd' : '#f5f5f5',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${message.agent === 'User' ? '#2196f3' : '#4caf50'}`
                  }}>
                    <strong>{message.agent}:</strong> {message.content}
                  </div>
                ))}
              </div>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Type your message here..."
                variant="outlined"
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                autoComplete="off"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SpeechButton
                        onResult={handleSpeechResult}
                        onPartialResult={handleSpeechPartialResult}
                        onStartListening={handleStartListening}
                        onClearTranscript={handleClearTranscript}
                        size="small"
                      />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};

export default AgentAssistantConversation; 